import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, TokenStore } from "@/api/http";
import {
  transactionReports,
  type TransactionReport,
} from "@/api/transactionReports";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Select,
} from "@/components/ui";

export function AdminTransactionReportsPage() {
  const [status, setStatus] = useState<TransactionReport["status"] | "">("PENDING");
  const qc = useQueryClient();
  const toast = useToast();

  const list = useQuery({
    queryKey: ["admin-rts22", status],
    queryFn: () =>
      transactionReports.list({ status: status || undefined }),
    refetchInterval: 30_000,
  });

  const submit = useMutation({
    mutationFn: () => transactionReports.submitPending(),
    onSuccess: (r) => {
      toast.push(
        `ARM submission: ${r.submitted}/${r.considered} ok, ${r.rejected} rejected`,
        r.rejected === 0 ? "success" : "warn",
      );
      qc.invalidateQueries({ queryKey: ["admin-rts22"] });
    },
    onError: (e) =>
      toast.push(
        e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e),
        "error",
      ),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">
            Transaction reporting (RTS 22)
          </h1>
          <p className="text-2xs text-term-mute">
            One row per reportable fill. Pending rows submit nightly via the
            configured ARM adapter; manual submit clears the queue on demand.
          </p>
        </div>
        <div className="flex gap-2">
          <Select
            className="w-44"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            <option value="PENDING">Pending</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="REJECTED">Rejected</option>
            <option value="">All</option>
          </Select>
          <Button onClick={() => submit.mutate()} loading={submit.isPending}>
            Submit pending now
          </Button>
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No reports"
              description="Reportable fills create rows here automatically. Pending rows are submitted nightly to the configured ARM."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">When</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Ref</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Status</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">ARM</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Submission ID</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Error</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">XML</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((r) => (
                  <tr key={r.id} className="border-t border-term-border">
                    <td className="px-3 py-1.5 text-term-dim">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-term-fg">
                      {r.transaction_reference_number.slice(0, 12)}…
                    </td>
                    <td className="px-3 py-1.5">{statusBadge(r.status)}</td>
                    <td className="px-3 py-1.5 text-term-dim">{r.arm_vendor ?? "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {r.arm_submission_id ?? "—"}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-red-500">
                      {r.last_error ?? ""}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <XmlButton id={r.id} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function statusBadge(s: TransactionReport["status"]) {
  const tone =
    s === "SUBMITTED"
      ? "good"
      : s === "REJECTED"
      ? "bad"
      : s === "PENDING"
      ? "warn"
      : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}

function XmlButton({ id }: { id: string }) {
  const toast = useToast();
  async function open() {
    try {
      const token = TokenStore.get();
      const resp = await fetch(transactionReports.xmlUrl(id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        toast.push(`XML fetch failed (${resp.status})`, "error");
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Don't immediately revoke — the new tab still needs the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.push(`XML fetch failed: ${(e as Error).message}`, "error");
    }
  }
  return (
    <Button variant="ghost" onClick={open}>
      View XML
    </Button>
  );
}
